/**
 * ReadinessSummaryCards - Score-Karten pro Channel
 */

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { ReadinessScoreBar } from './ReadinessScoreBar'
import type { ReadinessSummaryItem } from '@/lib/api/channel-readiness'

interface ReadinessSummaryCardsProps {
  summary: ReadinessSummaryItem[]
  selectedChannelId: string | null
  onSelect: (channelId: string) => void
}

export function ReadinessSummaryCards({
  summary,
  selectedChannelId,
  onSelect,
}: ReadinessSummaryCardsProps) {
  const { t } = useTranslation()
  if (summary.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500 text-sm">
        {t('common.noChannelsAvailable')}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {summary.map((item) => (
        <button
          key={item.channel_id}
          onClick={() => onSelect(item.channel_id)}
          className={cn(
            'p-4 rounded-xl border text-left transition-all',
            selectedChannelId === item.channel_id
              ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200 dark:ring-primary-800'
              : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-neutral-300 dark:hover:border-neutral-600'
          )}
        >
          <div className="font-semibold text-sm text-neutral-900 dark:text-neutral-100 mb-1">
            {item.channel_name}
          </div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
            {Math.round(item.avg_score)}%
          </div>
          <ReadinessScoreBar score={item.avg_score} size="sm" showLabel={false} />
          <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            {item.ready}/{item.total} {t('readiness.readyProducts')}
          </div>
          <div className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
            {item.image_rule_count} {t('common.image')} + {item.text_rule_count} {t('readiness.textRuleScore')}
          </div>
        </button>
      ))}
    </div>
  )
}
